import { command, getRequestEvent } from '$app/server';
import { error } from '@sveltejs/kit';
import { supabaseAdmin } from '#lib/server/supabaseAdmin.js';
import type { DailyMode, StatsRequest, StreakResponse } from '~/types';

export type AfterSignupResult =
  | { ok: true; streaks: Array<{ mode: DailyMode } & StreakResponse> }
  | { ok: false; error: string };

const STREAK_COLS =
  'mode, current_streak, best_streak, total_games, won_games';

export const afterSignup = command(
  'unchecked',
  async (stats: StatsRequest[]): Promise<AfterSignupResult> => {
    const { locals } = getRequestEvent();

    if (!locals.user) error(403, 'not signed in');

    try {
      await supabaseAdmin.from('user_device_map').upsert({
        device_id: locals.userId,
        user_id: locals.user.id,
      });

      const streaks: Array<{ mode: DailyMode } & StreakResponse> = [];

      for (const modeStats of stats) {
        const prev = await supabaseAdmin
          .from('daily_streaks')
          .select(STREAK_COLS)
          .eq('user_id', locals.user.id)
          .eq('mode', modeStats.mode)
          .single();

        if (!prev.data || prev.data.won_games <= modeStats.wonGames) {
          const upserted = await supabaseAdmin
            .from('daily_streaks')
            .upsert({
              user_id: locals.user.id,
              mode: modeStats.mode,
              best_streak: modeStats.bestStreak,
              current_streak: modeStats.currStreak,
              total_games: modeStats.totalGames,
              won_games: modeStats.wonGames,
            })
            .select(STREAK_COLS)
            .single();
          if (upserted.error) throw new Error(upserted.error.message);
          streaks.push(upserted.data as { mode: DailyMode } & StreakResponse);
        } else {
          streaks.push(prev.data as { mode: DailyMode } & StreakResponse);
        }
      }

      return { ok: true, streaks };
    } catch (e) {
      return {
        ok: false,
        error: e instanceof Error ? e.message : 'unknown error',
      };
    }
  },
);
